import React, { useState } from 'react';
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Plus, ChevronRight, ChevronDown, Package } from "lucide-react";
import { cn } from "../ui/utils";
import { Ticket, TicketCard } from "./TicketCard";

interface StatusConfigItem {
  label: string;
  color: string;
  kanbanColor: string;
}

interface KanbanViewProps {
  tickets: Ticket[];
  statusConfig: Record<string, StatusConfigItem>;
  onTicketMove: (ticketId: string, newStatus: string) => void;
  onTicketClick?: (ticket: Ticket) => void;
  canMove?: boolean;
}

export const TicketKanbanView: React.FC<KanbanViewProps> = ({ 
  tickets, 
  statusConfig, 
  onTicketMove,
  onTicketClick,
  canMove = true
}) => {
  const [activeLane, setActiveLane] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, ticketId: string) => {
    if (!canMove) {
        e.preventDefault();
        return;
    }
    e.dataTransfer.setData("ticketId", ticketId);
  };

  const handleDragEnd = () => {
    setActiveLane(null);
  };

  const handleDragOver = (e: React.DragEvent, lane: string) => {
    if (!canMove) return;
    e.preventDefault();
    setActiveLane(lane);
  };

  const handleDrop = (e: React.DragEvent, newStatus: string) => {
    if (!canMove) return;
    const ticketId = e.dataTransfer.getData("ticketId");
    onTicketMove(ticketId, newStatus);
    setActiveLane(null);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-8 min-h-[600px] md:h-[calc(100vh-350px)] custom-scrollbar">
      {Object.entries(statusConfig).map(([key, config]) => (
        <div 
          key={key} 
          onDragOver={(e) => handleDragOver(e, key)}
          onDragLeave={() => setActiveLane(null)}
          onDrop={(e) => handleDrop(e, key)}
          className={cn(
            "min-w-[280px] sm:min-w-[320px] w-[280px] sm:w-[320px] rounded-xl p-3 border transition-all duration-200 flex flex-col", 
            config.kanbanColor,
            activeLane === key ? "bg-gray-200 border-gray-300" : ""
          )}
        >
          <div className="flex justify-between items-center mb-4 px-1 shrink-0">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-white/80 shadow-sm text-gray-700 font-bold text-[11px] rounded px-2 py-0.5 uppercase border border-gray-100">
                {config.label}
              </Badge>
            </div>
            <span className="text-[11px] font-bold text-gray-400 bg-white/50 w-5 h-5 rounded-full flex items-center justify-center">
              {tickets.filter(t => t.status === key).length}
            </span>
          </div>
          
          <div className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-1 custom-scrollbar">
            {tickets.filter(t => t.status === key).map(ticket => (
              <TicketCard 
                key={ticket.id} 
                ticket={ticket} 
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                isLaneHighlighted={activeLane === key}
                onClick={() => onTicketClick?.(ticket)}
                canDrag={canMove}
                statusConfig={statusConfig}
                onMove={onTicketMove}
              />
            ))}
            
            {tickets.filter(t => t.status === key).length === 0 && (
              <div className="border-2 border-dashed border-gray-200/50 rounded-xl h-24 flex items-center justify-center text-gray-400 text-xs font-medium shrink-0">
                No tickets in this stage
              </div>
            )}
          </div>
        </div>
      ))}
      {/* 
      <div className="min-w-[100px] flex items-start pt-4">
        <Button variant="ghost" className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 h-10 w-10 p-0 rounded-full">
          <Plus size={24} />
        </Button>
      </div>
      */}
    </div>
  );
};
